/**
 * "select group sp500 and russell 2000" — groups for the screener's ◎ universe,
 * named in words (owner, 2026-10-21).
 *
 * Two readers: the command search builds its **Select groups** rows from a
 * typed phrase (`parseGroupPhrase`), and the `selectGroups` action resolves the
 * names the agent passes (`resolveGroup`). One matcher, so "S&P 500" means the
 * same group to both.
 *
 * ## A name matches by its letters and digits
 *
 * `sp500`, `S&P 500` and `s&p500` are one group, and `russell 2000` is
 * `Russell 2000` — so a name and a code are compared with case, spaces and
 * punctuation removed (`squash`). A few market nicknames are added on top
 * (`ALIASES`); nothing is fuzzy beyond that, because a near miss here screens a
 * different universe without saying so.
 *
 * ## No separator is needed
 *
 * The phrase is read greedily, the longest run of words that names a group
 * first, so `sp500 russell 2000` reads as two groups and `russell 2000` is never
 * split into `russell` and `2000`. A word that starts no group makes the whole
 * phrase no answer — a row naming two of three groups would quietly drop one.
 */

/** What the matcher needs of a group — `SymbolGroup`'s identifying half. */
export interface NamedGroup {
  code: string;
  name: string;
  groupType: string;
  market?: string | null;
}

/** Lower case, letters and digits only — `S&P 500` → `sp500`. */
export function squash(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

/** Nicknames, by group code. Only names a trader says for exactly that group. */
const ALIASES: Readonly<Record<string, readonly string[]>> = {
  SP500: ["spx", "sandp500"],
  NQ100: ["ndx", "nasdaq100", "nas100"],
  DJ30: ["dow", "dow30", "djia", "dowjones"],
  RU1000: ["r1k", "russell1k", "russell1000"],
  RU2000: ["r2k", "russell2k", "russell2000", "rut"],
  SP1500: ["sp1500"],
};

/** Every key that names one group: its name, its code, its nicknames. */
function keysOf(group: NamedGroup): string[] {
  return [squash(group.name), squash(group.code), ...(ALIASES[group.code] ?? [])];
}

/**
 * The group a name means, or `undefined`. A code is exact; a name or nickname
 * matches by its letters and digits. Two groups answering one name (the same
 * name in two markets) resolve to the first — callers that care pass the code.
 */
export function resolveGroup<G extends NamedGroup>(groups: readonly G[], name: string): G | undefined {
  const exact = groups.find((g) => g.code === name);
  if (exact) return exact;
  const key = squash(name);
  if (!key) return undefined;
  return groups.find((g) => keysOf(g).includes(key));
}

/** How the phrase joined its groups — `and`, `or`, or neither said. */
export type Joiner = "and" | "or" | null;

export interface GroupPhrase<G extends NamedGroup = NamedGroup> {
  groups: G[];
  /** `add group …` keeps what is already picked; every other verb replaces it. */
  add: boolean;
  joiner: Joiner;
}

/**
 * The verbs that make a phrase a group selection. `show` and `add` need the
 * word `group` after them: `show peers` and `add sma 20` mean other things.
 */
const LEAD =
  /^(?:(?:select|pick|choose|use|screen)(?:\s+(?:the\s+)?(?:groups?|universe|index(?:es)?))?|(?:show|add)\s+(?:the\s+)?(?:groups?|universe)|groups?|universe)\s+/;

/** The longest run of words a group can be named in — `nifty midcap 150`. */
const MAX_WORDS = 5;

export function parseGroupPhrase<G extends NamedGroup>(query: string, groups: readonly G[]): GroupPhrase<G> | null {
  const text = query.trim().toLowerCase().replace(/s\s*&\s*p/g, "sp");
  const lead = LEAD.exec(text);
  if (!lead) return null;
  const add = lead[0].startsWith("add");
  let joiner: Joiner = null;
  const words: string[] = [];
  for (const raw of text.slice(lead[0].length).split(/[\s,+&]+|(?=[,+&])/)) {
    const word = raw.replace(/[,+&]/g, "");
    if (!word) continue;
    if (word === "and" || word === "or") {
      joiner ??= word;
      continue;
    }
    if (word === "plus") {
      joiner ??= "and";
      continue;
    }
    if (word === "the") continue;
    words.push(word);
  }
  if (words.length === 0) return null;

  const found: G[] = [];
  for (let i = 0; i < words.length; ) {
    let match: G | undefined;
    let used = 0;
    for (let n = Math.min(MAX_WORDS, words.length - i); n >= 1 && !match; n--) {
      match = resolveGroup(groups, words.slice(i, i + n).join(""));
      if (match) used = n;
    }
    if (!match) return null;
    if (!found.some((g) => g.code === match!.code && g.groupType === match!.groupType)) found.push(match);
    i += used;
  }
  return { groups: found, add, joiner };
}
